import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { NativeSyntheticEvent, TextInputFocusEventData } from 'react-native';
import { useRecoilState } from 'recoil';
import { z } from 'zod';

import { productBlockFilterAtom, searchTermAtom } from '../../../store/ProducManagement/atom';

export const filterOptions = [
  { label: 'Todo', value: 'all' },
  { label: 'Com bloqueio', value: 'blocked' },
  { label: 'Sem bloqueio', value: 'unblocked' },
];

const formSchema = z.object({
  searchTerm: z
    .string({ required_error: 'Campo obrigatório' })
    .min(2, 'Campo deve ter no mínimo 2 caracteres')
    .refine((value) => value.trim() !== '', {
      message: 'Campo não pode consistir apenas de espaços em branco',
    }),
});

type FormData = z.infer<typeof formSchema>;

export const useFilterProductsManagement = () => {
  const [productBlockFilter, setProductBlockFilter] = useRecoilState(productBlockFilterAtom);
  const [searchTerm, setSearchTerm] = useRecoilState(searchTermAtom);

  const { reset, ...filterForm } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      searchTerm: '',
    },
  });

  const handleUpdateSearchTerm = useCallback(
    (data: FormData) => {
      setSearchTerm(data.searchTerm);
    },
    [setSearchTerm]
  );

  const handleInputBlur = useCallback(
    (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
      if (!e.nativeEvent.text) {
        reset({ searchTerm: '' });
        setSearchTerm('');
      }
    },
    [reset, setSearchTerm]
  );

  const handleClearSearchTerm = useCallback(() => {
    reset({ searchTerm: '' });
    setSearchTerm('');
  }, [reset, setSearchTerm]);

  const handleBlockTypeFilter = useCallback(
    (value: string) => {
      setProductBlockFilter(value);
    },
    [setProductBlockFilter]
  );

  const handleClearFilter = useCallback(() => {
    handleClearSearchTerm();
    setProductBlockFilter('all');
  }, [handleClearSearchTerm, setProductBlockFilter]);

  const memoizedFilters = useMemo(
    () => ({
      productBlockFilter,
      searchTerm,
    }),
    [productBlockFilter, searchTerm]
  );

  useEffect(() => {
    if (!searchTerm) {
      reset({ searchTerm: '' });
    }
  }, [reset, searchTerm]);

  const memoizedHandlers = useMemo(
    () => ({
      setSearchTerm: handleUpdateSearchTerm,
      handleInputBlur,
      handleClearSearchTerm,
      handleClearFilter,
      handleBlockTypeFilter,
    }),
    [
      handleUpdateSearchTerm,
      handleInputBlur,
      handleClearSearchTerm,
      handleBlockTypeFilter,
      handleClearFilter,
    ]
  );

  return {
    ...memoizedFilters,
    filterForm,
    ...memoizedHandlers,
  };
};
